import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AsyncSelect from "react-select/async";
import {
  adminCouponCreateAction,
  adminCouponGenerateCodeAction,
  adminCouponGetAction,
  adminCouponUpdateAction,
  adminUsersListAction
} from "../Action/action";

function userToOption(u) {
  if (!u?.id) return null;
  const name = (u.name || "User").toString().trim() || "User";
  const email = (u.email || "").toString().trim();
  return {
    value: Number(u.id),
    label: email ? `${name} (${email})` : name,
    name,
    email,
    user: u
  };
}

function formatUserOptionLabel(option, { context }) {
  if (context === "value") return option.name || option.label;
  if (option.email) return `${option.name || "User"} (${option.email})`;
  return option.name || option.label || "User";
}

async function loadUserOptions(inputValue) {
  const q = String(inputValue || "").trim();
  try {
    const res = await adminUsersListAction({
      page: 1,
      per_page: 25,
      search: q || undefined
    });
    const rows = Array.isArray(res?.data) ? res.data : [];
    return rows.map(userToOption).filter(Boolean);
  } catch (e) {
    return [];
  }
}

export default function CouponForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [discount, setDiscount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [userMaxUse, setUserMaxUse] = useState("");
  const [isActive, setIsActive] = useState(1);
  const [isUserSpecific, setIsUserSpecific] = useState(0);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [generatingCode, setGeneratingCode] = useState(false);

  const assignedUserOptions = useMemo(
    () => (assignedUsers || []).map(userToOption).filter(Boolean),
    [assignedUsers]
  );

  const userSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: fieldErrors.users ? "var(--danger)" : state.isFocused ? "#2B7BCA" : "#D1D5DB",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(43, 123, 202, 0.12)" : "none",
        fontSize: "13.5px",
        "&:hover": {
          borderColor: state.isFocused ? "#2B7BCA" : "#9CA3AF"
        }
      }),
      menu: (base) => ({ ...base, zIndex: 1060 }),
      menuList: (base) => ({ ...base, maxHeight: 280 }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "rgba(43, 123, 202, 0.12)"
      }),
      multiValueLabel: (base) => ({ ...base, color: "#1E5A96", fontSize: "12.5px" }),
      multiValueRemove: (base) => ({
        ...base,
        ":hover": { backgroundColor: "rgba(43, 123, 202, 0.22)", color: "#1F2937" }
      }),
      placeholder: (base) => ({ ...base, color: "#6B7280", fontSize: "13.5px" })
    }),
    [fieldErrors.users]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminCouponGetAction(id);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load coupon.");
          return;
        }
        const d = res.data || {};
        setName(d.name || "");
        setCode(d.code || "");
        setType(d.type === "amount" ? "amount" : "percent");
        setDiscount(d.discount != null ? String(d.discount) : "");
        setStartDate(d.start_date ? String(d.start_date).slice(0, 10) : "");
        setEndDate(d.end_date ? String(d.end_date).slice(0, 10) : "");
        setMaxUses(d.max_uses != null ? String(d.max_uses) : "");
        setUserMaxUse(d.user_max_use != null ? String(d.user_max_use) : "1");
        setIsActive(d.is_active !== undefined && d.is_active !== null ? (Number(d.is_active) ? 1 : 0) : 1);
        setIsUserSpecific(Number(d.is_user_specific) ? 1 : 0);
        setAssignedUsers(Array.isArray(d.users) ? d.users : []);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load coupon.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  const validate = () => {
    const errs = {};
    if (!String(name || "").trim()) errs.name = "Name is required.";
    const codeNorm = String(code || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!codeNorm) errs.code = "Code is required.";
    else if (!/^[A-Z0-9_-]{2,64}$/.test(codeNorm)) errs.code = "Use 2–64 letters, numbers, _ or -.";
    if (!discount || Number(discount) < 0 || Number.isNaN(Number(discount))) errs.discount = "Discount is required.";
    if (type === "percent" && Number(discount) > 100) errs.discount = "Percent cannot be more than 100.";
    if (startDate && endDate && endDate < startDate) errs.endDate = "End date cannot be before start date.";
    if (maxUses !== "" && (!/^\d+$/.test(maxUses) || Number(maxUses) < 1)) {
      errs.maxUses = "Use per coupon must be a positive number, or leave empty for unlimited.";
    }
    if (!userMaxUse || !/^\d+$/.test(userMaxUse) || Number(userMaxUse) < 1) {
      errs.userMaxUse = "Uses per customer must be at least 1.";
    }
    if (Number(isUserSpecific) && assignedUsers.length < 1) {
      errs.users = "Select at least one user for a user-specific coupon.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const generateCode = async () => {
    if (generatingCode || saving || loading) return;
    setGeneratingCode(true);
    setError("");
    try {
      const res = await adminCouponGenerateCodeAction(isEdit && id ? { exclude_id: id } : undefined);
      const next = String(res?.data?.code || "").trim().toUpperCase();
      if (!res?.success || !next) throw new Error(res?.message || res?.msg || "Could not generate code.");
      setCode(next);
      setFieldErrors((p) => ({ ...p, code: "" }));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Could not generate code.";
      setFieldErrors((p) => ({ ...p, code: msg }));
    } finally {
      setGeneratingCode(false);
    }
  };

  const onSave = async () => {
    if (saving) return;
    setError("");
    if (!validate()) return;

    const payload = {
      name: String(name || "").trim(),
      code: String(code || "").trim().toUpperCase().replace(/\s+/g, ""),
      type,
      discount: Number(discount),
      start_date: startDate || null,
      end_date: endDate || null,
      max_uses: maxUses === "" ? null : Number(maxUses),
      user_max_use: Number(userMaxUse),
      is_user_specific: Number(isUserSpecific),
      user_ids: Number(isUserSpecific) ? assignedUsers.map((u) => Number(u.id)) : [],
      is_active: Number(isActive)
    };

    setSaving(true);
    try {
      if (isEdit) {
        const res = await adminCouponUpdateAction(id, payload);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Update failed");
        toast.success("Coupon updated", { toastId: "coupon-save" });
      } else {
        const res = await adminCouponCreateAction(payload);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Create failed");
        toast.success("Coupon created", { toastId: "coupon-save" });
      }
      navigate("/coupons");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: "coupon-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {(loading || saving) && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      <div className="toolbar">
        <div className="breadcrumb">
          <Link to="/coupons">Coupons</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"} Coupon</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/coupons" className="btn btn-primary">
            ← Back
          </Link>
        </div>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Save failed.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="settings-section">
          <h3>Coupon Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label>
                Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setFieldErrors((p) => ({ ...p, name: "" }));
                }}
                placeholder="e.g. Pre-launch 50%"
                style={fieldErrors.name ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.name ? <div className="text-danger field-error">{fieldErrors.name}</div> : null}
            </div>
            <div className="form-group">
              <label>
                Code <span className="text-danger">*</span>
              </label>
              <div className="coupon-code-row">
                <input
                  type="text"
                  className="form-control"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase().replace(/\s+/g, ""));
                    setFieldErrors((p) => ({ ...p, code: "" }));
                  }}
                  placeholder="e.g. ALICE50"
                  style={fieldErrors.code ? { borderColor: "var(--danger)" } : undefined}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={generateCode}
                  disabled={generatingCode || saving || loading}
                >
                  {generatingCode ? "Generating…" : "Generate"}
                </button>
              </div>
              {fieldErrors.code ? <div className="text-danger field-error">{fieldErrors.code}</div> : null}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Type <span className="text-danger">*</span>
              </label>
              <select className="form-control" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="percent">Percent (%)</option>
                <option value="amount">Amount</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                Discount <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={discount}
                onChange={(e) => {
                  setDiscount(e.target.value);
                  setFieldErrors((p) => ({ ...p, discount: "" }));
                }}
                placeholder={type === "percent" ? "e.g. 50" : "e.g. 100.00"}
                style={fieldErrors.discount ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.discount ? <div className="text-danger field-error">{fieldErrors.discount}</div> : null}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setFieldErrors((p) => ({ ...p, endDate: "" }));
                }}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setFieldErrors((p) => ({ ...p, endDate: "" }));
                }}
                style={fieldErrors.endDate ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.endDate ? <div className="text-danger field-error">{fieldErrors.endDate}</div> : null}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Use Per Coupon</label>
              <input
                type="number"
                min="1"
                step="1"
                className="form-control"
                value={maxUses}
                onChange={(e) => {
                  setMaxUses(e.target.value);
                  setFieldErrors((p) => ({ ...p, maxUses: "" }));
                }}
                placeholder="Leave empty for unlimited"
                style={fieldErrors.maxUses ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.maxUses ? <div className="text-danger field-error">{fieldErrors.maxUses}</div> : null}
            </div>
            <div className="form-group">
              <label>
                Uses Per Customer <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                className="form-control"
                value={userMaxUse}
                onChange={(e) => {
                  setUserMaxUse(e.target.value);
                  setFieldErrors((p) => ({ ...p, userMaxUse: "" }));
                }}
                style={fieldErrors.userMaxUse ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.userMaxUse ? <div className="text-danger field-error">{fieldErrors.userMaxUse}</div> : null}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Coupon for</label>
              <select
                className="form-control"
                value={isUserSpecific}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setIsUserSpecific(v);
                  if (!v) setAssignedUsers([]);
                  setFieldErrors((p) => ({ ...p, users: "" }));
                }}
              >
                <option value={0}>All users (normal)</option>
                <option value={1}>Specific users</option>
              </select>
              <p className="small-text mt-2">
                {Number(isUserSpecific)
                  ? "Only the selected users can apply this code."
                  : "Anyone with the code can apply it."}
              </p>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={isActive} onChange={(e) => setIsActive(Number(e.target.value))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-row">
          {Number(isUserSpecific) ? (
              <div className="form-group">
                <label>
                  Assigned users <span className="text-danger">*</span>
                </label>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions
                  loadOptions={loadUserOptions}
                  formatOptionLabel={formatUserOptionLabel}
                  value={assignedUserOptions}
                  onChange={(opts) => {
                    const next = Array.isArray(opts) ? opts : [];
                    setAssignedUsers(
                      next.map((o) => o.user || { id: o.value, name: o.name || o.label, email: o.email }).filter((u) => u?.id)
                    );
                    setFieldErrors((p) => ({ ...p, users: "" }));
                  }}
                  placeholder="Search and select users…"
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? "No users found" : "Type to search users"
                  }
                  styles={userSelectStyles}
                  classNamePrefix="coupon-user-select"
                />
                <p className="small-text mt-2">Search by name, email or phone. You can select more than one.</p>
                {fieldErrors.users ? <div className="text-danger field-error">{fieldErrors.users}</div> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || loading}>
          {saving ? "Saving…" : isEdit ? "Update Coupon" : "Save Coupon"}
        </button>
        <Link to="/coupons" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
